import EmptyLogo from 'assets/images/bvaDetails/emptyLogo.png'

/** Flat fill used when `backgroundUrl` is missing or unreachable, matching the on-screen fallback in BrandDetails.tsx. */
export const PDF_BACKGROUND_FALLBACK_COLOR = '#F1F1F1'

interface ToDataUrlOptions {
  /** Downscale so the longest edge is at most this many pixels. */
  maxEdge?: number
  mimeType?: 'image/jpeg' | 'image/png'
  quality?: number
}

export const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Image decode failed: ${src.slice(0, 96)}`))
    image.src = src
  })

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })

const resize = async (dataUrl: string, options: ToDataUrlOptions): Promise<string> => {
  const { maxEdge, mimeType = 'image/jpeg', quality = 0.9 } = options
  if (!maxEdge) return dataUrl

  const image = await loadImage(dataUrl)
  const longest = Math.max(image.naturalWidth, image.naturalHeight)
  if (longest <= maxEdge && mimeType === 'image/png') return dataUrl

  const scale = Math.min(1, maxEdge / longest)
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))

  const context = canvas.getContext('2d')
  if (!context) return dataUrl

  if (mimeType === 'image/jpeg') {
    // JPEG has no alpha channel: without this, transparent pixels become black.
    context.fillStyle = '#FFFFFF'
    context.fillRect(0, 0, canvas.width, canvas.height)
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL(mimeType, quality)
}

/**
 * Remote URL -> data URI, so the image survives being serialized into the SVG
 * `foreignObject` html-to-image rasterizes (that context cannot load external
 * resources) and so the output canvas is never tainted.
 *
 * NOTE: `fetch` needs `Access-Control-Allow-Origin` on the response. An
 * `<img>` tag does not. So an image that renders fine on screen can still
 * fail here -- that is not a bug in this helper, it is a real CORS gap on the
 * remote resource.
 */
export const toDataUrl = async (url: string, options: ToDataUrlOptions = {}): Promise<string> => {
  if (url.startsWith('data:')) return options.maxEdge ? resize(url, options) : url

  const response = await fetch(url, { mode: 'cors', credentials: 'omit' })
  if (!response.ok) throw new Error(`Image fetch failed (${response.status}): ${url}`)

  const dataUrl = await blobToDataUrl(await response.blob())
  return options.maxEdge ? resize(dataUrl, options) : dataUrl
}

export interface PdfImageSources {
  /** Always a usable data URI -- falls back to the bundled empty-logo asset. */
  logo: string
  /** `null` means "render the flat PDF_BACKGROUND_FALLBACK_COLOR instead". */
  background: string | null
}

export const resolvePdfImages = async (details: {
  logoUrl?: string
  brandLogoUrl?: string
  backgroundUrl?: string
}): Promise<PdfImageSources> => {
  const logoCandidate = details.logoUrl || details.brandLogoUrl || EmptyLogo

  const [logo, background] = await Promise.all([
    toDataUrl(logoCandidate, { maxEdge: 640, mimeType: 'image/png' })
      // Remote logo unreachable -> bundled asset. `emptyLogo.png` is under
      // CRA's inline-size limit, so webpack has already turned it into a
      // `data:` URL and this branch needs no network access at all.
      .catch(() => toDataUrl(EmptyLogo).catch(() => EmptyLogo)),
    details.backgroundUrl
      ? toDataUrl(details.backgroundUrl, { maxEdge: 1920, mimeType: 'image/jpeg', quality: 0.86 }).catch(() => null)
      : Promise.resolve(null),
  ])

  return { logo, background }
}
